import React, { memo, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LineChart } from '@mui/x-charts/LineChart';
import { Loading } from '../../../../../common/ui';
import styles from './styles.module.css';

const SERIES_COLORS = [
  '#10B2D0',
  '#095664',
  '#98D0DA',
  '#F59E0B',
  '#8B5CF6',
  '#EF4444',
  '#22C55E',
  '#3B82F6',
  '#EC4899',
  '#14B8A6',
];

const DailyVarianceChart = memo(({ data = null, isLoading = false, error = null }) => {
  const chartWrapRef = useRef(null);
  const dates = data?.dates || [];
  const series = data?.series || [];
  const hasData = dates.length > 0 && series.length > 0;

  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const roundMarks = () => {
      const marks = wrap.querySelectorAll('.MuiChartsLegend-mark');
      marks.forEach((rect) => {
        const w = rect.getAttribute('width');
        const h = rect.getAttribute('height');
        if (w && h) {
          const r = Math.min(Number(w), Number(h)) / 2;
          rect.setAttribute('rx', r);
          rect.setAttribute('ry', r);
        }
      });
    };
    const id = requestAnimationFrame(roundMarks);
    return () => cancelAnimationFrame(id);
  }, [data]);

  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        id: String(item.id),
        label: item.label || 'Cashier',
        // Match sales chart: missing points become 0 so lines stay continuous.
        data: (item.data || []).map((value) =>
          value === null || value === undefined || Number.isNaN(Number(value))
            ? 0
            : Number(value)
        ),
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        showMark: false,
        curve: 'monotoneX',
      })),
    [series]
  );

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartCardTitle}>Day End Variance</div>
      <div className={styles.chartCardChart}>
        {isLoading ? (
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        ) : error ? (
          <div className={styles.empty}>{error}</div>
        ) : !hasData ? (
          <div className={styles.empty}>No chart data</div>
        ) : (
          <div ref={chartWrapRef} className={styles.chartWrap}>
            <LineChart
              className={styles.chart}
              xAxis={[
                {
                  id: 'days',
                  data: dates,
                  scaleType: 'point',
                },
              ]}
              series={chartSeries}
              height={260}
              margin={{ top: 16, right: 16, bottom: 75, left: 56 }}
              grid={{ horizontal: true }}
              aria-label="Day end variance by cashier over time"
              disableLineItemHighlight
              disableAxisListener
              slotProps={{
                legend: {
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  direction: 'row',
                  padding: { top: 16, bottom: 0, left: 0, right: 0 },
                },
              }}
              sx={{
                '& .MuiChartsAxis-tickLabel': {
                  fill: 'var(--gray-600)',
                  fontFamily: 'var(--font-family-base)',
                  fontSize: 'var(--font-size-sm)',
                },
                '& .MuiChartsAxis-line': {
                  stroke: 'var(--gray-200)',
                },
                '& .MuiChartsAxis-tick': {
                  stroke: 'var(--gray-200)',
                },
                '& .MuiChartsGrid-line': {
                  stroke: 'var(--gray-200)',
                },
                '& .MuiChartsLegend-label': {
                  fill: 'var(--black)',
                  fontFamily: 'var(--font-family-base)',
                  fontSize: 'var(--font-size-sm)',
                },
                '& .MuiChartsLegend-root text': {
                  fill: 'var(--black)',
                  fontFamily: 'var(--font-family-base)',
                  fontSize: 'var(--font-size-sm)',
                },
                '& .MuiChartsLegend-root tspan': {
                  fill: 'var(--black)',
                },
                '& .MuiLineElement-root': {
                  strokeWidth: 2,
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

DailyVarianceChart.propTypes = {
  data: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        data: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
        ),
      })
    ),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

DailyVarianceChart.defaultProps = {
  data: null,
  isLoading: false,
  error: null,
};

export default DailyVarianceChart;
